import { Controller } from "@hotwired/stimulus";
import SlimSelect from "slim-select";

/**
 * Controller for a <select /> to become a ChipMultiSelect widget.
 *
 * Fires the usual change event, but only when the Apply or Clear buttons are
 * clicked.
 *
 * Can be make searchable using the `isSearchable` value, which can be set by
 * the `is_searchable` kwarg from the Python widget.
 */
export default class extends Controller {
  static targets = [
    "select",
    "label",
    "buttonsTemplate",
    "applyButton",
    "clearButton",
    "contentContainer",
    "checkbox",
  ];

  static classes = [
    "max",
    "option",
    "placeholder",
    "value",
    "leftAlign",
    "rightAlign",
  ];

  static values = {
    menuAlignment: { type: String, default: "auto" }, // "auto" |"left" | "right".
    isSearchable: Boolean,
    placeholder: String,
  };

  initialize() {
    this.interceptSelectChange = this.handleInterceptSelectChange.bind(this);

    // Keeps track of the applied data of the select.
    this.appliedData = [];
  }

  /**
   * Returns an array of select target's <option /> elements.
   */
  get options() {
    return Array.from(this.selectTarget.querySelectorAll("option"));
  }

  /**
   * Returns the HTML for an option, with a checkbox that is checked if the
   * option is selected.
   *
   * SlimSelect's option objects have the same attributes as an <option />
   * element, so this works for both objects or elements.
   *
   * Every checkbox is set as a `checkbox` target so its its `checked` can be
   * changed when it connects, if it is selected or not.
   */
  // eslint-disable-next-line class-methods-use-this
  getOptionHtml({ selected, text, value }) {
    const checked = selected ? ' checked="checked"' : "";
    const html = `
      <label>
        <input ${checked}
               type="checkbox"
               value="${value}"
               data-${this.identifier}-target="checkbox" />
        ${text}
      </label>
    `;
    return html;
  }

  /**
   * Positions the content container of the select, aligned with the select's
   * left edge or right edge, depending on the available space.
   */
  positionContentContainer() {
    if (this.hasContentContainerTarget && this.select) {
      if (this.menuAlignmentValue === "left") {
        this.contentContainerTarget.classList.add(this.leftAlignClass);
      } else if (this.menuAlignmentValue === "right") {
        this.contentContainerTarget.classList.add(this.rightAlignClass);
      } else {
        // Auto positioning.
        const selectRect = this.select.render.main.main.getBoundingClientRect();
        const menuWidth = this.contentContainerTarget.offsetWidth;
        const spaceRight = window.innerWidth - selectRect.right;

        if (spaceRight < menuWidth && selectRect.left >= menuWidth) {
          // Not enough space on the right, but enough on the left.
          this.contentContainerTarget.classList.add(this.rightAlignClass);
          this.contentContainerTarget.classList.remove(this.leftAlignClass);
        } else {
          // Defaults to aligning with the left edge.
          this.contentContainerTarget.classList.add(this.leftAlignClass);
          this.contentContainerTarget.classList.remove(this.rightAlignClass);
        }
      }
    }
  }

  contentContainerTargetConnected() {
    this.positionContentContainer();
  }

  /**
   * Binds to the select's change before any external listeners, and sets up
   * the SlimSelect instance.
   */
  selectTargetConnected(target) {
    target.addEventListener("change", this.interceptSelectChange, {
      capture: true,
    });

    this.appliedData = this.options.map((option) => {
      // Handles the single select case where the empty value option is the
      // placeholder. Use `this.placeholderValue` as usual, and not the
      // option's text.
      const isPlaceholder = !target.multiple && option.value === "";

      return {
        html: isPlaceholder ? undefined : this.getOptionHtml(option),
        selected: option.selected,
        text: isPlaceholder ? this.placeholderValue : option.text,
        value: option.value,
        placeholder: isPlaceholder ? true : undefined,
      };
    });

    // Checks if the select should be disabled because its targets is disabled,
    // or because its target belongs to a disabled fieldset.
    const isDisabled = Boolean(
      target.disabled || target.closest("fieldset[disabled]"),
    );

    this.select = new SlimSelect({
      cssClasses: {
        max: this.maxClass,
        option: this.optionClass,
        placeholder: this.placeholderClass,
        value: this.valueClass,
      },
      data: this.appliedData,
      events: {
        /**
         * Always sets the data to the applied data.
         * Does not need to fire an event, as no new data was applied.
         */
        beforeClose: () => {
          this.select.setData(this.appliedData, false);
        },
      },
      select: target,
      settings: {
        allowDeselect: true,
        closeOnSelect: false,
        contentLocation: this.contentContainerTarget,
        contentPosition: "relative",
        disabled: isDisabled,
        maxValuesShown: 1,
        placeholderText: this.placeholderValue,
        showSearch: this.isSearchableValue,
      },
    });

    this.positionContentContainer();
  }

  /**
   * Marks each checkbox as checked or unchecked, depending on the current
   * selection.
   */
  checkboxTargetConnected(target) {
    const selection = new Set(this.select.getSelected());
    // eslint-disable-next-line no-param-reassign
    target.checked = selection.has(target.value);
  }

  /**
   * Clones the content of the `buttonsTemplate` targets and appends it to the
   * select's content.
   * The content here is the apply and clear buttons.
   */
  buttonsTemplateTargetConnected(target) {
    const clone = target.content.cloneNode(true);
    this.select.render.content.main.appendChild(clone);
  }

  /**
   * Clones the content of the `label` targets and appends it to the select's
   * values div.
   */
  labelTargetConnected(target) {
    const clone = target.content.cloneNode(true);
    this.select.render.main.main.prepend(clone);
  }

  /**
   * Called when the apply button is clicked.
   * Sets `appliedData` to the currently selected data, and closes the select.
   *
   * Dispatches a custom change event from the select target, which will be
   * allowed through for external listeners because it has the `expose`
   * property set to true.
   */
  applySelection() {
    this.appliedData = structuredClone(this.select.getData());
    this.select.setData(this.appliedData, false);
    this.select.close();
    this.dispatch("change", {
      detail: { expose: true },
      prefix: null,
      target: this.selectTarget,
    });
  }

  /**
   * Called when the clear button is clicked.
   * Clears the selection of the select target and closes the select.
   *
   * Dispatches a custom change event from the select target, which will be
   * allowed through for external listeners because it has the `expose`
   * property set to true.
   */
  clearSelection() {
    this.select.setSelected([]);
    this.applySelection();
  }

  /**
   * Captures the builtin select's change events and prevents them from
   * bubbling up, in favour of the custom change event fired by the
   * `applySelection` or `clearSelection` methods, which is allowed to go
   * through.
   */
  // eslint-disable-next-line class-methods-use-this
  handleInterceptSelectChange(event) {
    if (!event.detail || !event.detail.expose) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }

  selectTargetDisconnected(target) {
    this.select.destroy();
    target.removeEventListener("change", this.interceptSelectChange, {
      capture: true,
    });
  }
}
